// @flow
import { mapValues, omitBy } from 'lodash';
import { ALL_IN_SMALL_CASE } from '@src/constants';
import {
  getCurrentMLDRowDimensionValues,
  getMetricNameFromRowValues,
  getParentValuesOfGivenDimensionColumnForGivenMLDRow,
} from '@components/custom-discounts/utils';
import {
  doesArrayContainAll,
  getIfGivenRowIsANewlyAddedRow,
  getIfGivenValueLiesWithInTheClosedInterval,
  getLastElementOfArray,
  getObjectWithOnlyNonZeroValuesFromGivenObject,
  isEmptyObject,
} from '@src/common-utils/utils';
import { formatFloat2DP } from '@components/views/grid-utils/renderers';

import {
  getDiscountRangeForConnectorMetrics,
  getDiscountRangeForSingleSelectMetrics,
  isDimsValidForDropdownOptionBasedOnGuidanceForConnectors,
  isFlatDimsValidBasedOnGuidanceForNonConnectors,
  isRowDimsValidBasedOnUsageAndGuidanceForConnectors,
  isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
} from './validation-util';
import {
  getMultiSelectUsageForDimensionCombination,
  getUsageForSingleSelectAndKafkaMetrics,
  getUsageForSingleSelectAndNonKafkaMetrics,
} from './usage-utils';

const KAFKA_USAGE_TOOLTIP = 'Total Kafka Usage (List Price) excluding dedicated clusters';

// MLD Configuration
const MLD_FOR_FIELD_CONFIG = {
  ConnectNumTasks: {
    isMultiSelect: true,
    usageUnit: 'Tasks',
    usageColName: 'Avg. Monthly Usage',
    isKafkaMetric: false,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc:
      isDimsValidForDropdownOptionBasedOnGuidanceForConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForConnectors,
    usageCalculatorFunc: getMultiSelectUsageForDimensionCombination,
    discountRangeFunc: getDiscountRangeForConnectorMetrics,
  },
  FlinkPoolNumCFUs: {
    isMultiSelect: false,
    usageUnit: 'CFUs',
    usageColName: 'Avg. Monthly Usage',
    isKafkaMetric: false,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndNonKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaNetworkRead: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaNetworkWrite: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaStorage: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaNumCKUs: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaBase: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  KafkaPartition: {
    isMultiSelect: false,
    usageUnit: '$',
    usageColName: 'Usage',
    isKafkaMetric: true,
    usageTooltip: KAFKA_USAGE_TOOLTIP,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
  TableflowNumTopics: {
    isMultiSelect: false,
    usageUnit: 'Topics',
    usageColName: 'Avg. Monthly Usage',
    isKafkaMetric: false,
    isDimsValidForDropdownOptionBasedOnGuidanceFunc: isFlatDimsValidBasedOnGuidanceForNonConnectors,
    isRowDimsValidFunc: isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors,
    usageCalculatorFunc: getUsageForSingleSelectAndNonKafkaMetrics,
    discountRangeFunc: getDiscountRangeForSingleSelectMetrics,
  },
};

export const shouldShowUsageContainerForGivenMetric = (metricName) =>
  Object.keys(MLD_FOR_FIELD_CONFIG).includes(metricName);

export const isMetricValidForNonAdminBasedOnConfig = (metricName) =>
  Object.keys(MLD_FOR_FIELD_CONFIG).includes(metricName);

export const getMLDForFieldConfigBasedOnGivenMetric = (metricName) =>
  MLD_FOR_FIELD_CONFIG[metricName] ?? {};

export const shouldUseMultiSelectForUsage = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName)?.isMultiSelect === true;

// Kafka metrics that have special usage calculation logic
export const isKafkaMetric = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName)?.isKafkaMetric === true;

export const getUsageLabelWithUnitForMetric = (metricName) => {
  const { usageUnit, usageColName } = getMLDForFieldConfigBasedOnGivenMetric(metricName);
  return `${usageColName} (${usageUnit})`;
};

export const getUsageTooltipForMetric = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName)?.usageTooltip ?? null;

/**
 * Returns the appropriate guidance validator function for a given metric from config.
 * Falls back to non-connector validator if metric is not in config.
 */
const getDropdownOptionGuidanceValidator = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName)
    .isDimsValidForDropdownOptionBasedOnGuidanceFunc;

/**
 * Returns the appropriate row dimensions validator function for a given metric from config.
 * Used to validate if an entire row's dimension values are valid for non-admin users.
 *
 * @param {string} metricName - The metric name
 * @returns {Function} The row validator function from config
 */
const getRowDimsValidator = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName).isRowDimsValidFunc;

/**
 * Returns the appropriate usage calculator function for a given metric from config.
 * Used to calculate usage value for metrics in the usage column.
 *
 * @param {string} metricName - The metric name
 * @returns {Function} The usage calculator function from config
 */
export const getUsageCalculatorFuncForMetric = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName).usageCalculatorFunc;

export const getDiscountRangeFuncForMetric = (metricName) =>
  getMLDForFieldConfigBasedOnGivenMetric(metricName).discountRangeFunc;

// todo::MLD reuse from getDropdownText in FormFields
export const getMultiSelectUsageDropdownDisplayText = (options) => {
  const numberOfOptions = options.length;
  if (numberOfOptions === 0) {
    return 'Usage';
  }
  return numberOfOptions === 1 ? options[0]?.text : `${numberOfOptions} selected`;
};

export const getMultiSelectUsageOptions = (perElemUsageBasedOnLastColumnExplosion) => {
  return Object.entries(perElemUsageBasedOnLastColumnExplosion).map(([key, value]) => {
    const displayText = `${key} - ${formatFloat2DP(value)}`;
    return {
      key,
      text: displayText,
    };
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// MLD Discount Validation Utilities
// ═══════════════════════════════════════════════════════════════════════════════

// Get discount guidance range for a given usage and guidance ranges
// Returns { minDiscount, maxDiscount } or null if not found
export const getDiscountMinMaxRangeBasedOnGuidanceRangesAndUsageValue = (
  usageValue,
  guidanceRanges
) => {
  for (const range of guidanceRanges) {
    const minUsageForThisRange = range.min_usage;
    const maxUsageForThisRange =
      range.max_usage === -1 ? Number.POSITIVE_INFINITY : range.max_usage;

    const isUsageValueWithinTheCurrRange = getIfGivenValueLiesWithInTheClosedInterval(
      usageValue,
      minUsageForThisRange,
      maxUsageForThisRange
    );

    if (isUsageValueWithinTheCurrRange) {
      return {
        minDiscount: range.min_discount,
        maxDiscount: range.max_discount,
      };
    }
  }

  // No range match occurs only when the usage value falls below the lowest bucket's min_usage and is greater than 0 (i.e., in the (0, lowest_min_usage) gap).
  // The highest bucket always extends to infinity, so a usage value can never exceed all ranges — this fallback only applies to the lower end.
  return {
    minDiscount: 0,
    maxDiscount: 0,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Find matching guidance ranges for user's dimension selection
// ─────────────────────────────────────────────────────────────────────────────
// User selection won't have "all" (disabled for non-admins)
// Guidance keys may have "all" as wildcard (e.g., 'aws-all-mz-internet')
// Backend guarantees exactly ONE matching key (no overlapping keys)
const findMatchingGuidanceRangesForTheGivenMLDRow = (selectedDimsArray, guidanceData) => {
  for (const [key, value] of Object.entries(guidanceData)) {
    const keyDimArray = convertDimsKeyToDimArray(key);
    // This checks whether the user entered value falls into this bucket or not
    if (isFirstDimArraySubSetOfSecond(selectedDimsArray, keyDimArray)) {
      return value;
    }
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Get discount range for SINGLE-SELECT metrics (Kafka, Flink, etc.)
// ─────────────────────────────────────────────────────────────────────────────
// For non-admins: no "all" in selection → exactly ONE matching guidance key ( based on subset logic )
// Returns { min_discount, max_discount } directly, no array/intersection needed
export const getMinMaxDiscountRangeForGivenMLDSingleSelectRow = (
  selectedDims,
  metricName,
  mldGuidance
) => {
  // Step 1: Get usage - for Kafka metrics use aggregated usage, otherwise exact key match
  const usagesData = getUsagesForMetric(metricName, mldGuidance);
  const usageKey = convertDimArrayToDimsKey(selectedDims);
  const usageForNonKafkaMetrics = usagesData?.[usageKey] ?? 0;
  // Note: Usage Key will always have some value so ?? 0 is redundant, putting it just to be sure
  const usage = isKafkaMetric(metricName)
    ? getAggregatedUsageForKafkaMetricBasedOnGuidance(metricName, mldGuidance)
    : usageForNonKafkaMetrics;

  // Step 2: Find THE ONE matching guidance
  const guidanceData = getGuidanceForMetric(metricName, mldGuidance);
  const guidanceRanges = findMatchingGuidanceRangesForTheGivenMLDRow(selectedDims, guidanceData);

  // Step 3: Get discount range directly - single lookup
  return getDiscountMinMaxRangeBasedOnGuidanceRangesAndUsageValue(usage, guidanceRanges);
};

// ═══════════════════════════════════════════════════════════════════════════════
// MLD Connector (Multi-Select) Discount Validation Utilities
// ═══════════════════════════════════════════════════════════════════════════════

// Get tier number (1, 2, 3, ...) for a connector from tier mapping
// tierMapping: Map<tierNumber, Set<connectorName>>
// Returns tier number (key) or null if not found
export const getConnectorTierGivenConnectorNameAndTierMapping = (connectorName, tierMapping) => {
  for (const [tierNumber, connectorSet] of tierMapping.entries()) {
    if (connectorSet.has(connectorName)) {
      return tierNumber;
    }
  }
  return null;
};

/**
 * Checks if the first dimension array is a subset of the second dimension array.
 *
 * Note: This function DOES NOT WORK for cases where the first dimension array contains "all".
 * An error will be thrown if the first array contains "all" to prevent incorrect usage.
 * Arrays are not allowed in the first dimension array and will throw an error.
 *
 * Examples:
 *
 * @example
 * // Valid subset: exact match
 * isFirstDimArraySubSetOfSecond(['aws', 'basic', 'mz'], ['aws', 'basic', 'mz'])
 * // Returns: true
 *
 * @example
 * // Valid subset: second array has "all" which matches any value
 * isFirstDimArraySubSetOfSecond(['aws', 'basic', 'mz'], ['aws', 'all', 'mz'])
 * // Returns: true
 *
 * @example
 * // Valid subset: second array has array which contains the first value
 * isFirstDimArraySubSetOfSecond(['aws', 'basic', 'mz'], ['aws', ['basic', 'standard'], 'mz'])
 * // Returns: true
 *
 * @example
 * // Not a subset: values don't match
 * isFirstDimArraySubSetOfSecond(['aws', 'basic', 'mz'], ['azure', 'basic', 'mz'])
 * // Returns: false
 *
 * @example
 * // Not a subset: first value not in second array
 * isFirstDimArraySubSetOfSecond(['aws', 'freight', 'mz'], ['aws', ['basic', 'standard'], 'mz'])
 * // Returns: false
 *
 * @example
 * // Error: first array contains "all" (not supported)
 * isFirstDimArraySubSetOfSecond(['aws', 'all', 'mz'], ['aws', 'basic', 'mz'])
 * // Throws: Error("first dimension array contains 'all', which is not supported")
 *
 * @example
 * // Error: first array contains array (not supported)
 * isFirstDimArraySubSetOfSecond(['aws', ['basic'], 'mz'], ['aws', 'basic', 'mz'])
 * // Throws: Error("first dimension array contains an array, which is not supported")
 *
 * @example
 * // Error: array length mismatch
 * isFirstDimArraySubSetOfSecond(['aws', 'basic'], ['aws', 'basic', 'mz'])
 * // Throws: Error("Dimension array length mismatch, 2 is not equal to 3")
 *
 * @example
 * // Case-insensitive comparison
 * isFirstDimArraySubSetOfSecond(['AWS', 'BASIC'], ['aws', 'basic'])
 * // Returns: true
 *
 * @param {(string|string[])[]} firstDimArray - The first dimension array (must not contain "all" or arrays)
 * @param {(string|string[])[]} secondDimArray - The second dimension array (may contain "all" or arrays)
 * @returns {boolean} True if firstDimArray is a subset of secondDimArray, false otherwise
 * @throws {Error} If array lengths don't match, if firstDimArray contains "all", or if firstDimArray contains arrays
 */
export const isFirstDimArraySubSetOfSecond = (firstDimArray, secondDimArray) => {
  // Check if array lengths match
  if (firstDimArray.length !== secondDimArray.length) {
    throw new Error(
      `Dimension array length mismatch, ${firstDimArray.length} is not equal to ${secondDimArray.length}`
    );
  }

  // Check if first dimension array contains arrays (not allowed)
  if (firstDimArray.some((val) => Array.isArray(val))) {
    throw new Error('first dimension array contains an array, which is not supported');
  }

  // Check if first dimension array contains "all" (case-insensitive)
  if (doesArrayContainAll(firstDimArray)) {
    throw new Error("first dimension array contains 'all', which is not supported");
  }

  /*
  Case 1: val1 is abc, val2 is [def, ghi] => can return FALSE
  Case 2: val1 is abc, val2 is [abc, def, ghi] => TRUE for this index, move forward
  Case 3: val1 is abc, val2 is [abc] => TRUE for this index, move forward
  Case 4: val1 is abc, val2 is [def] => can return FALSE
  Case 5: val1 is abc, val2 is [all] => TRUE for this index, move forward
   */

  for (let i = 0; i < firstDimArray.length; i++) {
    // Val1 is guaranteed to be a string
    let val1 = firstDimArray[i];
    val1 = val1.toLowerCase();

    // Val2 can be an array or a string, normalize it into Array
    let val2 = secondDimArray[i];
    val2 = Array.isArray(val2) ? val2 : [val2];
    val2 = val2.map((x) => x.toLowerCase());

    if (val2.includes(ALL_IN_SMALL_CASE)) {
      continue;
    }

    if (!val2.includes(val1)) {
      return false;
    }
  }

  return true;
};

/**
 * Converts a usage data key to a dimension array format.
 * Keys like "aws-standard-mz-internet" are split by dashes into arrays.
 * Single keys like "aws" become single-element arrays.
 *
 * @param {string} key - The usage data key
 * @returns {string[]} Array representation of the key
 */
export const convertDimsKeyToDimArray = (key) => {
  if (!key || typeof key !== 'string' || key.length === 0) {
    return [];
  }
  // Split by dashes to handle compound keys like "aws-standard-mz-internet"
  return key.split('-');
};

export const convertDimArrayToDimsKey = (dimsArray, delimiter = '-') => {
  return dimsArray.join(delimiter);
};

export const getAggregatedUsageForKafkaStorageMetric = (usagesData, guidanceData) => {
  const guidanceKeys = Object.keys(guidanceData);
  let aggregatedUsage = 0;

  for (const [usageKey, usageValue] of Object.entries(usagesData)) {
    const usageKeyAsDimArray = convertDimsKeyToDimArray(usageKey);

    const isTableflowEnabledColumnValue = getLastElementOfArray(usageKeyAsDimArray);
    if (isTableflowEnabledColumnValue === 'true') {
      continue;
    }

    if (getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys(usageKeyAsDimArray, guidanceKeys)) {
      aggregatedUsage += usageValue;
    }
  }

  return aggregatedUsage;
};

/**
 * Aggregates usage values for all usage keys that are present in guidance.
 *
 * For Kafka metrics, we need to sum up all usages where the usage key matches
 * any guidance key (using subset logic). This is used when displaying total
 * usage for a metric based on what guidance covers.
 *
 * @example
 * // usagesData: { 'aws-standard-mz': 100, 'aws-basic-sz': 50, 'azure-standard-mz': 75 }
 * // guidanceData: { 'aws-all-mz': [...], 'azure-standard-mz': [...] }
 * // 'aws-standard-mz' matches 'aws-all-mz' (subset) → included (100)
 * // 'aws-basic-sz' does NOT match any guidance key → excluded
 * // 'azure-standard-mz' matches 'azure-standard-mz' (exact) → included (75)
 * // Returns: 175
 *
 * @param {Object} usagesData - Usage data object { 'dim1-dim2-dim3': usageValue, ... }
 * @param {Object} guidanceData - Guidance data object { 'dim1-dim2-dim3': guidanceRanges, ... }
 * @returns {number} Sum of all usage values where usage keys are covered by guidance
 */
export const getAggregatedUsageForAllUsageKeysBasedOnGuidance = (usagesData, guidanceData) => {
  const guidanceKeys = Object.keys(guidanceData);
  let aggregatedUsage = 0;

  for (const [usageKey, usageValue] of Object.entries(usagesData)) {
    const usageKeyAsDimArray = convertDimsKeyToDimArray(usageKey);
    if (getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys(usageKeyAsDimArray, guidanceKeys)) {
      aggregatedUsage += usageValue;
    }
  }

  return aggregatedUsage;
};

export const getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys = (
  dimsArray,
  arrayOfKeysToCheck
) => {
  const lengthOfDimsArray = dimsArray.length;
  // If the dimsArray is only partially filled, then, we need to only consider that portion of the key
  // e.g., if dimsArray is aws-standard-mz-internet and key is [aws, all], then, only aws-standard needs to be considered
  for (const dimensionKey of arrayOfKeysToCheck) {
    let relevantDimensionKeyArray = dimensionKey?.split('-') ?? [];
    relevantDimensionKeyArray = relevantDimensionKeyArray.slice(0, lengthOfDimsArray);

    if (isFirstDimArraySubSetOfSecond(dimsArray, relevantDimensionKeyArray)) {
      return true;
    }
  }

  return false;
};

export const getIfGivenConnectorFallsIntoAnyTierProvidedByGuidanceObject = (
  connectorName,
  connectTierMappingMap
) => {
  if (!connectTierMappingMap || !connectorName) {
    return false;
  }

  for (const tierSet of connectTierMappingMap.values()) {
    if (tierSet.has(connectorName)) {
      return true;
    }
  }

  return false;
};

/**
 * Validates if a dimension value should be enabled in dropdown based on guidance.
 * Uses config-based validator lookup to handle metric-specific logic.
 *
 * @param {string} metricName - The metric name
 * @param {Object} mldGuidance - The MLD guidance object
 * @param {string[]} partiallyFilledDimsArray - Dimension values selected so far
 * @returns {boolean} True if option should be enabled
 */
const isDimsValidForDropdownOptionBasedOnGuidance = (
  metricName,
  mldGuidance,
  partiallyFilledDimsArray
) => {
  const dropdownOptionGuidanceValidator = getDropdownOptionGuidanceValidator(metricName);
  return dropdownOptionGuidanceValidator(mldGuidance, partiallyFilledDimsArray, metricName);
};

/**
 * Validates if the partially filled dimensions or flat dims array(array of strings) are valid based on usage data.
 *
 * This function is used for both dropdown option validation for all metrics and row validation for non-connector metrics
 *
 *
 * This function works for ALL metrics during dropdown option validation including connectors because:
 * - For dropdown option validation, we check a single option at a time
 * - The dims array is always an array of strings (not array of arrays like multi-select scenarios)
 * - This makes the subset check logic consistent across all metric types
 *
 * During row validation, we need to validate the entire row dimensions array and hence, this will work only for non-connector metrics
 *
 * @param {string} metricName - The metric name to validate dimensions for
 * @param {Object} mldGuidance - The MLD guidance object containing usages and guidance data
 * @param {Array<string>} partiallyFilledDimsArray - Array of dimension values filled so far
 * @returns {boolean} - True if the dimensions are valid based on usage, false otherwise
 */
export const isFlatDimsValidBasedOnUsage = (metricName, mldGuidance, partiallyFilledDimsArray) => {
  const usages = getUsagesForMetric(metricName, mldGuidance);
  if (!usages) {
    return false;
  }

  /*
    For non-connectors, e.g, CKUs, Usages will never contain all and also non-admins cannot give all
    So, partiallyFilledDimsArray will not contain all and none of Object.keys(usages) will contain all
    So, this subset logic will work - although it is an overkill
    But, keeping it like this for now, to help future use cases, if any
   */

  return getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys(
    partiallyFilledDimsArray,
    Object.keys(usages)
  );
};

export const isDropdownOptionValidForNonAdmin = ({
  columnConfig,
  rowValues,
  dimensionValue,
  metricName,
  mldGuidance,
}) => {
  const parentDimValuesArray = getParentValuesOfGivenDimensionColumnForGivenMLDRow(
    columnConfig,
    rowValues
  );

  const partiallyFilledDimsArrayIncludingThisIndex = [...parentDimValuesArray, dimensionValue];

  return isFlatDimsValidBasedOnUsageAndGuidance({
    metricName,
    mldGuidance,
    dimensionValuesArray: partiallyFilledDimsArrayIncludingThisIndex,
  });
};

/**
 * Checks if the given dimension values are valid according to usage and guidance.
 * This function is used for dropdown option disabling and
 * it is also used for row validation for non-connector metrics
 *
 * @param {string} metricName - The name of the metric
 * @param {Object} mldGuidance - The mld guidance object
 * @param {string[]} dimensionValuesArray - The array of dimension values
 * @returns {boolean} True if dimensions are valid, false otherwise
 */
export const isFlatDimsValidBasedOnUsageAndGuidance = ({
  metricName,
  mldGuidance,
  dimensionValuesArray,
}) => {
  const isValidAccordingToUsage = isFlatDimsValidBasedOnUsage(
    metricName,
    mldGuidance,
    dimensionValuesArray
  );

  const isValidAccordingToGuidance = isDimsValidForDropdownOptionBasedOnGuidance(
    metricName,
    mldGuidance,
    dimensionValuesArray
  );

  return isValidAccordingToUsage && isValidAccordingToGuidance;
};

/**
 * Applies non-admin disabling rules to a dropdown options list, including "ALL".
 * "ALL" is disabled by default for non-admins.
 * For any non-ALL option, if it is not valid according to usage and guidance, then, it is disabled for non-admins.
 */
export const getDimensionsOptionsByAddingDisabledForFieldBasedOnGuidanceAndUsage = ({
  metricName,
  rowValues,
  dropdownOptions,
  mldGuidance,
  columnConfig,
}) => {
  /*
  Note: For non-admins, for non-connectors, we should blindly disable ALL as an option
  For connectors, it is handled inside MultiSelectFieldWithCheckboxes and we can ignore that case here
   */

  return dropdownOptions.map((option) => {
    const dimensionValue = option?.name?.toLowerCase();
    const isAllOption = dimensionValue === ALL_IN_SMALL_CASE;
    const isValid = isAllOption
      ? false
      : isDropdownOptionValidForNonAdmin({
          columnConfig,
          rowValues,
          dimensionValue,
          metricName,
          mldGuidance,
        });
    return {
      ...option,
      shouldNotBeSelectable: !isValid, // If it is not valid for a non-admin, then, it should not be selectable
    };
  });
};

/**
 * Checks if a metric is present in BOTH ae_discount_guidance AND usages.
 * A metric must exist in both to be enabled for non-admins.
 * we don't have to check for zero usage values as they are filtered out in the estimate context hook itself.
 */
export const getIfMetricHasGuidanceAndUsage = (metricName, mldGuidance) => {
  const { metricNameToUsagesMap, metricNameToAEDiscountGuidanceMap } = mldGuidance;
  const existsInUsages = metricNameToUsagesMap?.[metricName] ?? false;
  const existsInGuidance = metricNameToAEDiscountGuidanceMap?.[metricName] ?? false;
  return existsInUsages && existsInGuidance;
};

export const getMetricsByAddingDisabledForFieldBasedOnGuidanceAndUsage = (
  metricsList,
  mldGuidance
) => {
  // For Field, mark metrics which don't have guidance OR usage as shouldNotBeSelectable
  return metricsList.map((metric) => {
    const isMetricSelectable = getIfMetricHasGuidanceAndUsage(metric.name, mldGuidance);
    return {
      ...metric,
      shouldNotBeSelectable: !isMetricSelectable,
    };
  });
};

export const getGuidanceForMetric = (metricName, { metricNameToAEDiscountGuidanceMap }) => {
  return metricNameToAEDiscountGuidanceMap?.[metricName] ?? null;
};

export const getUsagesForMetric = (metricName, { metricNameToUsagesMap }) => {
  return metricNameToUsagesMap?.[metricName] ?? null;
};

/**
 * Retrieves the pre-computed aggregated usage for a Kafka metric from mldGuidance.
 *
 * @param {string} metricName - The Kafka metric name.
 * @param {Object} mldGuidance - MLD guidance object containing metricNameToAggregatedUsageMapBasedOnGuidanceForKafkaMetrics.
 * @returns {number} Aggregated usage value for the metric, or 0 if not found.
 */
export const getAggregatedUsageForKafkaMetricBasedOnGuidance = (
  metricName,
  { metricNameToAggregatedUsageMapBasedOnGuidanceForKafkaMetrics }
) => {
  return metricNameToAggregatedUsageMapBasedOnGuidanceForKafkaMetrics[metricName] ?? 0;
};

// For Kafka metrics, we need to aggregate the usage across all dimension combinations
export const getMetricNameToAggregatedUsageMapForKafkaMetrics = (
  metricNameToUsagesMap,
  metricNameToAEDiscountGuidanceMap
) => {
  const aggregatedUsageMap = {};
  let aggregatedUsageForKafkaStorageMetricCachedValue = null;
  for (const [metricName] of Object.entries(metricNameToUsagesMap)) {
    if (isKafkaMetric(metricName)) {
      const guidanceMap = metricNameToAEDiscountGuidanceMap[metricName];

      if (!guidanceMap) {
        continue;
      }

      /*
       * For now, we are only taking Kafka Storage aggregated values for all the Kafka metrics.
       * This is because of the following problem:
       *
       * We know from the requirements that for all Kafka metrics, the usage value is the total spend.
       *
       * So, if we can get the aggregated usage for Kafka Storage, we can use it for all the Kafka metrics.
       *
       * We are only using Kafka Storage because it is the only Kafka metric which will work for all rate cards and all dimension combinations.
       * We cannot use other Kafka metrics because of the below reasons:
       *
       * We cannot use Kafka Num CKUs because for older rate cards, we don't have the CKU usage data for standard clusters.
       *
       * We cannot use KafkaNetworkRead and KafkaNetworkRead, because for enterprise multi networking clusters,
       * the keys under usages are split into private and pnl for networking types from multi which is affecting the usage aggregation logic
       * as BE will attach total spend to all the usage keys which are even in the same cluster for ex: If total spend is 150,
       * then, under usages, for aws-enterprise-mz-pni: 150, aws-enterprise-mz-pnl: 150, the total usage will be 300 instead of 150.
       *
       * Even for Kafka Storage, we are excluding all the keys with 'true' value for the tableflow_enabled column.
       * Otherwise, the usage aggregation logic will be doubled which is not correct. But, because Kafka Storage metric
       * will be present in all the rate cards and for all dimension combinations, we are using Kafka Storage
       *
       * This problem primarily araised because we are using the same usage keys for both disabling the dimension values and also for usage calculation.
       * and for Kafka metrics alone, the usage calculated follows different logic.
       *
       * In future, when this goes away, we need to go to the version control history and get the original code
       */
      if (aggregatedUsageForKafkaStorageMetricCachedValue == null) {
        aggregatedUsageForKafkaStorageMetricCachedValue = getAggregatedUsageForKafkaStorageMetric(
          metricNameToUsagesMap.KafkaStorage,
          metricNameToAEDiscountGuidanceMap.KafkaStorage
        );
      }

      aggregatedUsageMap[metricName] = aggregatedUsageForKafkaStorageMetricCachedValue;
    }
  }
  return aggregatedUsageMap;
};

/**
 * Checks if all dimension values in the row are valid according to usage and guidance.
 * If any dimension value is ALL, then, we return false as ALL is not a valid value for a non-admin.
 * Returns true if dimensions are valid, false otherwise.
 * Uses config-based validator lookup to handle metric-specific logic (connectors vs non-connectors) for row validation.
 *
 * For non-admin users, validates that existing row dimensions are within allowed values.
 * Returns true for admin users or newly added rows (where user is forced to select valid values).
 *
 * @param {Object} rowValues - The row values from formik
 * @param {Object} mldGuidance - The MLD guidance object
 * @param {boolean} isUserAdmin - Whether the current user is an admin
 * @returns {boolean} True if dimensions are valid, false otherwise
 */
export const isCurrentRowDimensionsValidBasedOnUsageAndGuidanceForField = ({
  rowValues,
  mldGuidance,
  isUserAdmin,
}) => {
  // If the user is an admin user, then, all the rows are valid for them
  if (isUserAdmin) {
    return true;
  }

  const isRowNewlyAdded = getIfGivenRowIsANewlyAddedRow(rowValues);

  // If it is a newly added row, then, the non-admin user is forced to select only the allowed values
  if (isRowNewlyAdded) {
    return true;
  }

  const currentRowDimValuesArray = getCurrentMLDRowDimensionValues(rowValues);

  // If any dimension value is ALL, then, we return false as ALL is not a valid value for a non-admin.
  if (currentRowDimValuesArray.includes(ALL_IN_SMALL_CASE)) {
    return false;
  }

  const metricName = getMetricNameFromRowValues(rowValues);

  // If the metric name is not present in the MLD_FOR_FIELD_CONFIG, then, we return false as it is not a valid metric for a non-admin.
  if (!isMetricValidForNonAdminBasedOnConfig(metricName)) {
    return false;
  }

  // Use config-based validator lookup
  const rowValidatorBasedOnUsageAndGuidance = getRowDimsValidator(metricName);
  return rowValidatorBasedOnUsageAndGuidance(mldGuidance, currentRowDimValuesArray, metricName);
};

/**
 * Filters the metric usage map by removing zero-value dimensions
 * and empty metric objects after filtering out zero values.
 */
export const getMetricUsageMapWithOnlyNonZeroValuesAndNonEmptyMetricObjects = (metricUsageMap) => {
  // Step 1: Transform each metric's usage map (remove zero values)

  /*
   * mapValues is a lodash function that returns a new object with the same keys
   * but with values transformed by the given function.
   *
   * @param {Object} obj - The source object to map.
   * @param {Function} iteratee - The function to transform each value.
   * @returns {Object} - The new object with the transformed values.
   *
   * Example:
   * const obj = { a: 1, b: 2, c: 3 };
   * const newObj = mapValues(obj, (value) => value * 2);
   * // newObj will be { a: 2, b: 4, c: 6 }
   */
  const metricsWithNonZeroValues = mapValues(
    metricUsageMap,
    getObjectWithOnlyNonZeroValuesFromGivenObject
  );

  // Step 2: Filter out metrics with empty usage maps

  /*
   * omitBy is a lodash function that returns a new object with properties
   * omitted based on the predicate function. Properties for which the
   * predicate returns truthy are excluded from the result.
   *
   * @param {Object} obj - The source object to filter.
   * @param {Function} predicate - The function invoked per property.
   * @returns {Object} - The new object without the omitted properties.
   *
   * Example:
   * const obj = { a: 1, b: 0, c: 3, d: null };
   * const newObj = omitBy(obj, (value) => value === 0);
   * // newObj will be { a: 1, c: 3, d: null }
   */
  return omitBy(metricsWithNonZeroValues, isEmptyObject);
};

/**
 * For a CC Starter Commit (Launch / Launch Pro) estimate, adds a zero usage for every guidance key of
 * every Kafka metric that has no usage yet.
 *
 * A regular estimate only lets a non-admin edit a dimension that has been sized, which is why zero usages
 * are dropped above. A package is the opposite: its Kafka discounts are generated for every dimension up
 * front, and a non-admin may raise any of them from the pack's floor up to the "no sizing" ceiling
 * (floor + 5) whether or not it is sized. A zero usage lands in the first (no sizing) guidance bucket, so
 * the allowed range comes straight from the backend's guidance.
 *
 * Only Kafka is opened up: with zero sizing the DSP discounts (Flink, Tableflow, Connect) stay locked for
 * non-admins. Real usage is never overwritten, and the input maps are not mutated.
 *
 * @param {Object} metricNameToUsagesMap - { metricName: { dimsKey: usage } }, zero usages already removed
 * @param {Object} metricNameToAEDiscountGuidanceMap - { metricName: { dimsKey: guidanceRanges } }
 * @returns {Object} a new usages map
 */
export const getMetricUsageMapWithZeroUsageForUnsizedKafkaGuidanceKeys = (
  metricNameToUsagesMap,
  metricNameToAEDiscountGuidanceMap
) => {
  const usageMapWithUnsizedKafkaKeys = { ...metricNameToUsagesMap };

  for (const [metricName, guidanceForMetric] of Object.entries(
    metricNameToAEDiscountGuidanceMap ?? {}
  )) {
    if (!isKafkaMetric(metricName) || !guidanceForMetric) {
      continue;
    }

    usageMapWithUnsizedKafkaKeys[metricName] = {
      ...mapValues(guidanceForMetric, () => 0),
      ...metricNameToUsagesMap[metricName],
    };
  }

  return usageMapWithUnsizedKafkaKeys;
};
